import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    fetchProductQuotationById,
    createProductQuotation,
    updateProductQuotation,
    fetchServiceQuotationById,
    createServiceQuotation,
    updateServiceQuotation,
    fetchAllProductQuotations,
    fetchAllServiceQuotations,
    fetchServiceQuotationDynamicDropdown,
    fetchProductQuotationDynamicDropdown,
    handleProductQuotationStatus,
    handleServiceQuotationStatus
} from "./thunk";

// --- Types based on backend response ---

interface QuotationDetail {
    quotation_number: string;
    quotation_date: string;
    enquiry: string; // UUID or ID
    agent: string; // ID
    company: string; // ID
    status: string;
    // ... other quotation fields from your actual response structure
}

interface AssignedMemberDetail {
    user: string; // UUID
}

interface ItemDetail {
    category: string; // UUID
    sub_category: string; // UUID
    make: string; // UUID
    model: string; // UUID
    quantity: number;
    // ... other item fields
}

interface QuotationStateData {
    quotation_details: QuotationDetail;
    assigned_member_details: AssignedMemberDetail[];
    item_details: ItemDetail[];
    // Add other top-level fields if your response includes them
}

// --- Initial State Definition ---

interface QuotationState {
    // Common states
    loading: boolean;
    error: string | null;
    rowCount: number;
    // State for forms (single quotation data)
    currentQuotation: QuotationStateData | null;
    quotationDropdown: any[]
    // States for table/list views
    quotationsList: QuotationStateData[];
    // State for successful creation/update (useful for redirects)
    operationSuccess: boolean;
}

const initialQuotationState: QuotationState = {
    loading: false,
    error: null,
    rowCount: 0,
    currentQuotation: null,
    quotationsList: [],
    operationSuccess: false,
    quotationDropdown: []
};

// --- Quotation Slice ---

// --- Quotation Slice ---

const quotationSlice = createSlice({
    name: "Quotation",
    initialState: initialQuotationState,
    reducers: {
        // Reducer to clear the state, e.g., when navigating away from the form
        clearQuotationState: (state) => {
            state.loading = false;
            state.error = null;
            state.currentQuotation = null;
            state.operationSuccess = false;
        },
    },
    extraReducers: (builder) => {
        // --- Shared Pending and Rejected Handlers ---
        // Note: These handlers are for the fulfillment logic below.
        const handlePending = (state: QuotationState) => {
            state.loading = true;
            state.error = null;
            state.operationSuccess = false;
        };

        const handleRejected = (state: QuotationState, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload || "An unknown error occurred";
            state.operationSuccess = false;
        };

        // --- FETCH BY ID (Product & Service) ---
        builder
            .addCase(fetchProductQuotationById.pending, handlePending)
            .addCase(fetchProductQuotationById.rejected, handleRejected)
            .addCase(fetchProductQuotationById.fulfilled, (state, action: PayloadAction<QuotationStateData>) => {
                state.loading = false;
                state.currentQuotation = action.payload;
                state.error = null;
            })
            .addCase(fetchServiceQuotationById.pending, handlePending)
            .addCase(fetchServiceQuotationById.rejected, handleRejected)
            .addCase(fetchServiceQuotationById.fulfilled, (state, action: PayloadAction<QuotationStateData>) => {
                state.loading = false;
                state.currentQuotation = action.payload;
                state.error = null;
            });

        // --- CREATE (Product & Service) ---
        builder
            .addCase(createProductQuotation.pending, handlePending)
            .addCase(createProductQuotation.rejected, handleRejected)
            .addCase(createProductQuotation.fulfilled, (state, action: PayloadAction<QuotationStateData>) => {
                state.loading = false;
                state.operationSuccess = true;
                state.error = null;
            })
            .addCase(createServiceQuotation.pending, handlePending)
            .addCase(createServiceQuotation.rejected, handleRejected)
            .addCase(createServiceQuotation.fulfilled, (state, action: PayloadAction<QuotationStateData>) => {
                state.loading = false;
                state.operationSuccess = true;
                state.error = null;
            });

        // --- UPDATE (Product & Service) ---
        builder
            .addCase(updateProductQuotation.pending, handlePending)
            .addCase(updateProductQuotation.rejected, handleRejected)
            .addCase(updateProductQuotation.fulfilled, (state, action: PayloadAction<QuotationStateData>) => {
                state.loading = false;
                state.currentQuotation = action.payload;
                state.operationSuccess = true;
                state.error = null;
            })
            .addCase(updateServiceQuotation.pending, handlePending)
            .addCase(updateServiceQuotation.rejected, handleRejected)
            .addCase(updateServiceQuotation.fulfilled, (state, action: PayloadAction<QuotationStateData>) => {
                state.loading = false;
                state.currentQuotation = action.payload;
                state.operationSuccess = true;
                state.error = null;
            });

        // --- FETCH ALL (List View) ---
        builder
            .addCase(fetchAllProductQuotations.pending, handlePending)
            .addCase(fetchAllProductQuotations.rejected, handleRejected)
            .addCase(fetchAllProductQuotations.fulfilled, (state, action: PayloadAction<{ data: QuotationStateData[]; rowCount: number }>) => {
                state.loading = false;
                state.quotationsList = action.payload.data;
                state.rowCount = action.payload.rowCount;
                state.error = null;
            })
            .addCase(fetchAllServiceQuotations.pending, handlePending)
            .addCase(fetchAllServiceQuotations.rejected, handleRejected)
            .addCase(fetchAllServiceQuotations.fulfilled, (state, action: PayloadAction<{ data: QuotationStateData[]; rowCount: number }>) => {
                state.loading = false;
                state.quotationsList = action.payload.data;
                state.rowCount = action.payload.rowCount;
                state.error = null;
            });

        // --- FETCH DROPDOWN ---
        builder
            .addCase(fetchProductQuotationDynamicDropdown.pending, handlePending)
            .addCase(fetchProductQuotationDynamicDropdown.rejected, handleRejected)
            .addCase(fetchProductQuotationDynamicDropdown.fulfilled, (state, action: PayloadAction<{ data: any[] }>) => {
                state.loading = false;
                state.quotationDropdown = action.payload.data;
                state.error = null;
            })
            .addCase(fetchServiceQuotationDynamicDropdown.pending, handlePending)
            .addCase(fetchServiceQuotationDynamicDropdown.rejected, handleRejected)
            .addCase(fetchServiceQuotationDynamicDropdown.fulfilled, (state, action: PayloadAction<{ data: any[] }>) => {
                state.loading = false;
                state.quotationDropdown = action.payload.data;
                state.error = null;
            });
        builder
            .addCase(handleProductQuotationStatus.pending, handlePending)
            .addCase(handleProductQuotationStatus.rejected, handleRejected)
            .addCase(handleProductQuotationStatus.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.operationSuccess = true;
                state.error = null;
            })
            .addCase(handleServiceQuotationStatus.pending, handlePending)
            .addCase(handleServiceQuotationStatus.rejected, handleRejected)
            .addCase(handleServiceQuotationStatus.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.operationSuccess = true;
                state.error = null;
            });
    },
});


export const { clearQuotationState } = quotationSlice.actions;

export default quotationSlice.reducer;
